import { nanoid } from "nanoid";
import { customStorageEventName } from "@vueuse/core";
import { getResolvedStoreId } from "./useStoreIdentity";

// Computer ID ผูกกับ "ร้าน" ไม่ใช่ "เครื่อง"
//
// ของเดิมเก็บ `computerUuid` key เดียวทั้งเครื่อง เครื่องที่ login สลับหลายร้าน
// (เช่นเครื่องเทส / เครื่อง support) เลยได้ ID เดียวกันทุกร้าน และไม่รู้เลยว่า ID
// นี้เกิดตั้งแต่เมื่อไหร่ — ตอนไล่เคส "เครื่องซ้ำ" ใน presence แยกไม่ออก
//
// ตอนนี้เก็บแยกต่อร้าน:
//   computerUuid_{storeId}              → ID ของเครื่องนี้ในร้านนี้
//   computerUuidCreatedAt_{storeId}     → ISO เวลาที่สร้าง ID
//   computerUuidCreatedAtEstimated_{storeId} = "1" → ID ย้ายมาจากของเดิม ไม่รู้เวลาสร้างจริง
//   computerRemark_{storeId}            → ชื่อเครื่อง เช่น "แคชเชียร์ชั้น 2"
//
// `computerUuid` ตัวเดิมยังเขียนทับเป็น ID ของร้านที่ใช้อยู่เสมอ โค้ดเก่าที่อ่าน
// useLocalStorage("computerUuid") (pos/payment/drawer/receipts/deviceBlock)
// จะได้ทำงานเหมือนเดิมโดยไม่ต้องแก้
export const LEGACY_COMPUTER_UUID_KEY = "computerUuid";
export const LEGACY_COMPUTER_REMARK_KEY = "computerRemark";
/** storeId ที่ "อ้างสิทธิ์" ID เดิมไปแล้ว — ร้านอื่นห้าม migrate ซ้ำ ไม่งั้นได้ ID ซ้ำกันข้ามร้าน */
export const COMPUTER_UUID_MIGRATED_FROM_KEY = "computerUuid_migratedFrom";

// ID เดิมที่มีอยู่ "ก่อน" boot รอบนี้ — ถ่ายไว้ตอนโหลด module (ก่อน plugin/store ไหนรัน)
// auth.init ตอนยังไม่รู้ร้าน (offline / auth timeout) จะสร้าง `computerUuid` ใหม่ด้วย
// nanoid(6) ID แบบนั้นต้องนับเป็นเครื่องใหม่ ไม่ใช่ "ย้ายมาจากของเดิม (estimated)"
const legacyIdAtBoot = safeGet(LEGACY_COMPUTER_UUID_KEY);

export const computerIdentityKeys = (storeId: string) => ({
  id: `computerUuid_${storeId}`,
  createdAt: `computerUuidCreatedAt_${storeId}`,
  createdAtEstimated: `computerUuidCreatedAtEstimated_${storeId}`,
  name: `computerRemark_${storeId}`,
});

export interface ComputerIdentity {
  storeId: string;
  id: string;
  name: string;
  createdAt: string | null;
  /** true = ID ย้ายมาจาก key เดิม createdAt คือ "เห็นครั้งแรก" ไม่ใช่เวลาสร้างจริง */
  createdAtEstimated: boolean;
}

/**
 * ให้ร้าน storeId มี Computer ID ของตัวเอง แล้วชี้ `computerUuid` เดิมไปที่ ID นี้
 * idempotent — เรียกซ้ำได้ (plugin ตอน boot + shop.init อีกรอบ)
 */
export function ensureComputerIdentity(storeId: string): ComputerIdentity | null {
  if (!storeId) return null;

  const keys = computerIdentityKeys(storeId);
  let id = safeGet(keys.id);

  if (!id) {
    const legacyId = safeGet(LEGACY_COMPUTER_UUID_KEY);
    const claimedBy = safeGet(COMPUTER_UUID_MIGRATED_FROM_KEY);
    const now = new Date().toISOString();

    if (legacyId && (!claimedBy || claimedBy === storeId)) {
      // ร้านแรกที่ boot หลังอัปเดตรับ ID เดิมไป ยอดลิ้นชัก/ใบเสร็จที่กรองตาม
      // computerUuid ของเครื่องนี้จะได้ไม่หายไปเฉยๆ
      id = legacyId;
      safeSet(keys.id, id);
      safeSet(keys.createdAt, now);
      // ID ที่เพิ่งเกิดใน boot นี้ (ดู legacyIdAtBoot) รู้เวลาสร้างจริง ไม่ต้องติด estimated
      if (legacyId === legacyIdAtBoot) safeSet(keys.createdAtEstimated, "1");
      else safeRemove(keys.createdAtEstimated);
      const legacyRemark = safeGet(LEGACY_COMPUTER_REMARK_KEY);
      if (legacyRemark && !safeGet(keys.name)) safeSet(keys.name, legacyRemark);
      safeSet(COMPUTER_UUID_MIGRATED_FROM_KEY, storeId);
      console.info(`[computerId] migrate "${LEGACY_COMPUTER_UUID_KEY}"=${id} -> "${keys.id}"`);
    } else {
      id = nanoid(6);
      safeSet(keys.id, id);
      safeSet(keys.createdAt, now);
      safeRemove(keys.createdAtEstimated);
      // เครื่องใหม่เอี่ยม (ไม่มี ID เดิม) ก็ต้อง claim ไว้ ไม่งั้นร้านถัดไปจะมาหยิบ
      // ID ของร้านนี้ (ที่เพิ่งเขียนลง key เดิม) ไปใช้ซ้ำ
      if (!claimedBy) safeSet(COMPUTER_UUID_MIGRATED_FROM_KEY, storeId);
    }
  } else if (!safeGet(keys.createdAt)) {
    // มี ID แต่ไม่มีวันที่ (เช่นตั้ง ID เองจากหน้า settings ก่อนมีระบบนี้)
    safeSet(keys.createdAt, new Date().toISOString());
    safeSet(keys.createdAtEstimated, "1");
  }

  syncLegacyKeys(storeId);
  return readComputerIdentity(storeId);
}

/** อ่านอย่างเดียว ไม่สร้าง — storeId ไม่ส่งมาจะใช้ร้านที่ resolve แล้ว */
export function readComputerIdentity(storeId: string = getResolvedStoreId()): ComputerIdentity | null {
  if (!storeId) return null;
  const keys = computerIdentityKeys(storeId);
  const id = safeGet(keys.id);
  if (!id) return null;
  return {
    storeId,
    id,
    name: safeGet(keys.name) || "",
    createdAt: safeGet(keys.createdAt),
    createdAtEstimated: safeGet(keys.createdAtEstimated) === "1",
  };
}

/** หน้า settings แก้ ID/ชื่อเครื่อง — เปลี่ยน ID = นับเป็นเครื่องใหม่ ได้ createdAt ใหม่ */
export function updateComputerIdentity(
  storeId: string,
  patch: { id?: string; name?: string }
): ComputerIdentity | null {
  if (!storeId) return null;
  const keys = computerIdentityKeys(storeId);

  if (patch.id != null && patch.id !== safeGet(keys.id)) {
    safeSet(keys.id, patch.id);
    safeSet(keys.createdAt, new Date().toISOString());
    safeRemove(keys.createdAtEstimated);
  }
  if (patch.name != null) safeSet(keys.name, patch.name);

  syncLegacyKeys(storeId);
  return readComputerIdentity(storeId);
}

export const useComputerIdentity = () => ({
  ensure: ensureComputerIdentity,
  read: readComputerIdentity,
  update: updateComputerIdentity,
  keys: computerIdentityKeys,
});

// ---------------------------------------------------------------------------

// เขียน key เดิมแล้วยิง event ของ vueuse ด้วย ref useLocalStorage("computerUuid")
// ที่เปิดค้างอยู่ใน tab เดียวกันจะได้ค่าใหม่ทันที (storage event ปกติไม่ยิงใน tab ตัวเอง)
function syncLegacyKeys(storeId: string) {
  const keys = computerIdentityKeys(storeId);
  writeShared(LEGACY_COMPUTER_UUID_KEY, safeGet(keys.id));
  // เผื่อ rollback ไป build เก่าที่หน้า settings อ่าน `computerRemark`
  writeShared(LEGACY_COMPUTER_REMARK_KEY, safeGet(keys.name) || "");
}

function writeShared(key: string, value: string | null) {
  if (value == null) return;
  const oldValue = safeGet(key);
  if (oldValue === value) return;
  safeSet(key, value);
  try {
    window.dispatchEvent(
      new CustomEvent(customStorageEventName, {
        detail: { key, oldValue, newValue: value, storageArea: localStorage },
      })
    );
  } catch {}
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / quota เต็ม — ได้ ID แค่ใน tab นี้
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {}
}
